/// <amd-module name="Widgets/MarkdownWidget/MarkdownWidget" />
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetPresentationMode, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { showActionRequiredDialog } from "Reporting/Lightbox/Lightbox";
import * as ReportingResources from "Reporting/Visuals/Resources";
import { ActionRequiredContent } from "Reporting/Visuals/VisualMessages/ActionRequiredContent";
import { StakeholderAccessRestrictedMessage } from "Reporting/Visuals/VisualMessages/StakeholderAccessRestrictedMessage";
import { getProject, getRepoType } from "Tfs/Widget/Context";
import * as Git from "VC/Common/Contracts/Git";
import { IVCNavigationService } from "VC/Common/Navigation";
import { VCImageTransformer } from "VC/Markdown/Plugins/VCImageTransformer";
import { VCLinkTransformer } from "VC/Markdown/Plugins/VCLinkTransformer";
import { MarkdownRenderer } from "VSS/Features/Markdown/MarkdownRenderer";
import { MarkdownRendererOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent } from "VSS/Platform/Layout";
import * as MarkdownResources from "Widgets/MarkdownWidget/Resources";
import { VCPathInformation, VersionControlFileContentDataProvider } from "./VersionControlDataProvider";

export interface IMarkdownWidgetState {
    markdown?: string;
    shouldBlockStakeholder?: boolean;
}

export class MarkdownWidget extends VssComponent<WidgetProps, IMarkdownWidgetState> {
    // Inspect the provided urls for all protocols we want to prevent
    // The markdown-it defaults are to allow the data protocol if it is an image but
    // we don't want to allow embeded images that we'll have to scan for malicious content
    private static BAD_PROTOCOLS: RegExp = /^(vbscript|javascript|file|data):/;
    public static readonly componentType = "markdownWidget";
    public static readonly stakeholderDataProvider = "ms.vss-dashboards-web.dashboards-content-provider";
    private static validateLinkCallback(url: string) {
        const str = url.trim().toLowerCase();
        return !MarkdownWidget.BAD_PROTOCOLS.test(str);
    }

    /**
     * Gets a markdown renderer object, uses dependency injection for testing.
     */
    public static getMarkdownRenderOptions(
        context: IVssPageContext,
        markdownVCItem?: Git.ItemModel,
        pathInfo?: VCPathInformation
    ): MarkdownRendererOptions {
        let imageTransformer: VCImageTransformer | undefined = undefined;
        let linkTransformer: VCLinkTransformer | undefined = undefined;

        const options: MarkdownRendererOptions = {
            disableToc: true,
            validateLink: MarkdownWidget.validateLinkCallback,
            enableImageSize: true,
            hideExternalImageIcon: true
        };

        if (markdownVCItem && pathInfo) {
            const repoType = getRepoType(pathInfo.path);
            
            if (pathInfo.version){
                const gitItemVersion: Git.GitVersionDescriptor = {
                    version: VersionControlFileContentDataProvider.getBranchNameWithoutPrefix(pathInfo),
                    versionOptions: Git.GitVersionOptions.None,
                    versionType: Git.GitVersionType.Branch
                }
                imageTransformer = new VCImageTransformer(repoType, markdownVCItem, pathInfo.repositoryId, getProject(context).id, context, gitItemVersion);

                linkTransformer = new VCLinkTransformer(
                    repoType,
                    markdownVCItem,
                    pathInfo.repositoryId,
                    getProject(context).name,
                    context,
                    pathInfo && pathInfo.version
                );
            }
            else {
                imageTransformer = new VCImageTransformer(repoType, markdownVCItem, pathInfo.repositoryId, getProject(context).id, context);

                linkTransformer = new VCLinkTransformer(
                    repoType,
                    markdownVCItem,
                    pathInfo.repositoryId,
                    getProject(context).name,
                    context,
                    pathInfo && pathInfo.version
                );
            }
            options["imageUrlTransformer"] = imageTransformer.transformImage;
            options["linkTransformer"] = linkTransformer.transformLink;
        }

        return options;
    }

    /**
     * Attempts to parse path information from a string and returns null on failure
     * @param customSettingsData - The string to be parsed from
     */
    public static parseVCPathInformation(customSettingsData: string | null): VCPathInformation | undefined {
        let pathInformation: VCPathInformation | undefined;
        if (customSettingsData) {
            try {
                pathInformation = JSON.parse(customSettingsData);
                // Because this is a free form, user could potentially parsing in a block of JSON data.
                // Verify this is a well formed VCPathInformation, and reset if not
                if (!(pathInformation && pathInformation.path)) {
                    pathInformation = undefined;
                }
            } catch (e) {}
        }
        return pathInformation;
    }
    private _renderer: MarkdownRenderer;

    constructor(props: WidgetProps) {
        super(props);
        this.state = {};
    }

    public async componentDidMount() {
        await this.setMarkdownContentState();
        WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
    }

    public async componentDidUpdate(prevProps: WidgetProps) {
        if (this.props.settings !== prevProps.settings) {
            await this.setMarkdownContentState();
        }
    }

    public render(): JSX.Element {
        if (this.state.shouldBlockStakeholder) {
            return this.renderStakeholderExperience();
        }

        const res = this._renderer ? this._renderer.renderHtml(this.state.markdown || "") : "";
        const resolvedMarkdown = {
            __html: res
        };
        const additionalCssClass =
            this.props.presentationMode == WidgetPresentationMode.lightboxView
                ? " lightboxed" // Add the space so the class can be seperate from the prepended classes
                : "";
        return (
            <div className={"markdown-widget" + additionalCssClass}>
                <div className="markdown-widget-container">
                    <div className="rendered-markdown" dangerouslySetInnerHTML={resolvedMarkdown} />
                </div>
            </div>
        );
    }

    private async setMarkdownContentState() {
        const settings = this.getMarkdownSettingsOrDefault(this.props.settings);
        const pathInfo = MarkdownWidget.parseVCPathInformation(settings);
        if (pathInfo) {
            if (this.userIsStakeholder()) {
                this.setState({ shouldBlockStakeholder: true }, this.props.onWidgetLoaded);
                return; // We are done here
            }
            const versionControlDataProvider = new VersionControlFileContentDataProvider(this.context.pageContext);
            this.addCustomContextMenuOption(pathInfo);
            try {
                const markdownFile = await versionControlDataProvider.getFile(pathInfo);
                const markdown = markdownFile ? markdownFile.content : "";
                this._renderer = new MarkdownRenderer(
                    this.context.pageContext,
                    MarkdownWidget.getMarkdownRenderOptions(this.context.pageContext, markdownFile, pathInfo)
                );
                this.setState({ markdown }, this.props.onWidgetLoaded);
            } catch (error) {
                this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error });
            }
        } else {
            const markdown = settings;
            this.clearCustomContextMenuOptions();
            this._renderer = new MarkdownRenderer(this.context.pageContext, MarkdownWidget.getMarkdownRenderOptions(this.context.pageContext));
            this.setState({ markdown }, this.props.onWidgetLoaded);
        }
    }

    private getMarkdownSettingsOrDefault(settings: string | null): string {
        if (settings == null) {
            return MarkdownResources.DefaultMarkdown;
        }
        return settings;
    }

    private addCustomContextMenuOption(pathInfo: VCPathInformation): void {
        this.props.setCustomContextMenuItems([
            {
                name: MarkdownResources.ContextMenu_FileLink,
                key: "file-link",
                iconProps: { className: "bowtie-icon bowtie-tfvc-raw-source" },
                onClick: () => onClickFPS(this.context.pageContext, this.getVCUrl(pathInfo), true)
            }
        ]);
    }

    private clearCustomContextMenuOptions(): void {
        this.props.setCustomContextMenuItems([]);
    }

    private getVCUrl(pathInfo: VCPathInformation): string {
        const navigationService = this.context.pageContext.getService<IVCNavigationService>("IVCNavigationService");
        const url = navigationService.getRepositoryUrl(
            pathInfo.repositoryId,
            getProject(this.context.pageContext).name,
            this.props.teamContext.name,
            undefined,
            { path: pathInfo.path }
        );
        return url;
    }

    private userIsStakeholder(): boolean {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<{ value: { IsStakeholder: boolean } }>(MarkdownWidget.stakeholderDataProvider);
        return data !== undefined && data.value && data.value.IsStakeholder;
    }

    private renderStakeholderExperience(): JSX.Element {
        return (
            <ActionRequiredContent
                visualTitle={this.props.name}
                onClick={() => {
                    showActionRequiredDialog(
                        this.context.pageContext,
                        ReportingResources.Stakeholder_DialogTitle,
                        <StakeholderAccessRestrictedMessage />
                    );
                }}
            />
        );
    }
}

VssComponent.register(MarkdownWidget.componentType, MarkdownWidget);
